import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import type { MonitorAndProtectModelAdsRequestAdType } from "@similarweb/pro-api-client";
import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import { filterFactory, MultiIncludeExcludeFilter } from "@similarweb/pro-logic-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpNavigation } from "../../../app/navigation";
import { adsTracking } from "../../../services/tracking/ads";
import { DomainsIncludeExcludeRenderer } from "../../../shared/components/filter-factory-items/domains-include-exclude/include-exclude-renderer";
import { KeywordsIncludeExcludeRenderer } from "../../../shared/components/filter-factory-items/keywords-include-exclude/include-exclude-renderer";
import { createIncludeExcludeLabel, createMultiSelectLabel } from "../../../shared/filters/label-helpers";
import { useMonitorAndProtectDataAndParams } from "../../../shared/hooks/navigations";

import type { IAdsContentRule, IAdsFilterState, IIncludeExcludeValue } from "./ads-filters.types";

const Filters = filterFactory.createTypedFilters<IAdsFilterState>();

interface IAdTypeOption {
    id: MonitorAndProtectModelAdsRequestAdType;
    value: MonitorAndProtectModelAdsRequestAdType;
    label: string;
    icon: "pla" | "text" | "serp-paid-with-links";
    [key: string]: unknown;
}

type AdsFilterName = keyof IAdsFilterState;

const normalizeIncludeExclude = (value: IIncludeExcludeValue | undefined): IIncludeExcludeValue | undefined => {
    if (!value) {
        return undefined;
    }
    const { include = [], exclude = [] } = value;
    if (include.length === 0 && exclude.length === 0) {
        return undefined;
    }
    return { include, exclude };
};

const cleanAdContent = (rules: IAdsContentRule[] | undefined): IAdsContentRule[] | undefined => {
    if (!rules) {
        return undefined;
    }
    const nonEmpty = rules.filter((rule) => rule.items.length > 0);
    return nonEmpty.length > 0 ? nonEmpty : undefined;
};

const DEFAULT_AD_CONTENT_RULES: IAdsContentRule[] = [
    { type: "include", fieldName: "any", matchType: "partial", items: [] },
];

export const AdsDataFilters = () => {
    const translate = useTranslation();
    const { dataFilters } = useMonitorAndProtectDataAndParams();
    const [state, setState] = useState<IAdsFilterState>(dataFilters as IAdsFilterState);

    const filterLabels = useMemo<Record<AdsFilterName, string>>(
        () => ({
            domains: translate("monitor_and_protect.page.ads.filters.domains.title"),
            adType: translate("monitor_and_protect.page.ads.filters.adType.title"),
            keywords: translate("monitor_and_protect.page.ads.filters.keywords.title"),
            adContent: translate("monitor_and_protect.page.ads.filters.adContent.title"),
            avgRank: translate("monitor_and_protect.page.ads.filters.rank.title"),
        }),
        [translate],
    );

    const adTypeOptions: IAdTypeOption[] = useMemo(
        () => [
            {
                id: "pla",
                value: "pla",
                label: translate("monitor_and_protect.page.ads.filters.adType.options.productListing"),
                icon: "pla",
            },
            {
                id: "paid",
                value: "paid",
                label: translate("monitor_and_protect.page.ads.filters.adType.options.text"),
                icon: "text",
            },
            {
                id: "paid_sitelinks",
                value: "paid_sitelinks",
                label: translate("monitor_and_protect.page.ads.filters.adType.options.paidSitelink"),
                icon: "serp-paid-with-links",
            },
        ],
        [translate],
    );

    const domainsTooltip = useMemo<ReactNode[] | undefined>(() => {
        const items: ReactNode[] = [];
        const { include = [], exclude = [] } = state.domains ?? {};
        if (include.length > 0) {
            items.push(
                <Box
                    key="included"
                    maxWidth="250px"
                >
                    {translate("monitor_and_protect.shared.filters.included_label")}: {include.join(", ")}
                </Box>,
            );
        }
        if (exclude.length > 0) {
            items.push(
                <Box
                    key="excluded"
                    maxWidth="250px"
                >
                    {translate("monitor_and_protect.shared.filters.excluded_label")}: {exclude.join(", ")}
                </Box>,
            );
        }
        return items.length > 0 ? items : undefined;
    }, [state.domains, translate]);

    const adContentDropDownsOptions = useMemo(
        () => [
            {
                ruleProperty: "type",
                options: [
                    { id: "include", title: translate("global.include") },
                    { id: "exclude", title: translate("global.exclude") },
                ],
                buttonWidth: 100,
                width: 200,
            },
            {
                ruleProperty: "fieldName",
                options: [
                    {
                        id: "any",
                        title: translate("monitor_and_protect.page.ads.filters.adContent.fields.anyAdPart"),
                    },
                    {
                        id: "title",
                        title: translate("monitor_and_protect.page.ads.filters.adContent.fields.title"),
                    },
                    {
                        id: "description",
                        title: translate("monitor_and_protect.page.ads.filters.adContent.fields.description"),
                    },
                ],
                buttonWidth: 140,
                width: 250,
            },
            {
                ruleProperty: "matchType",
                options: [
                    {
                        id: "partial",
                        title: translate("monitor_and_protect.page.ads.filters.adContent.matchTypes.contains"),
                    },
                    {
                        id: "exact",
                        title: translate("monitor_and_protect.page.ads.filters.adContent.matchTypes.exact"),
                    },
                ],
                buttonWidth: 140,
                width: 200,
            },
        ],
        [translate],
    );

    const handleFilterChange = (newState: IAdsFilterState) => {
        const cleaned: IAdsFilterState = {
            ...newState,
            adContent: cleanAdContent(newState.adContent),
        };
        setState(cleaned);
        mnpNavigation.updateQueryParams({ dataFilters: cleaned });
    };

    const renderRankLabel = (value: { from?: number; to?: number } | undefined): ReactNode => {
        const rankLabel = filterLabels.avgRank;
        if (!value || (value.from === undefined && value.to === undefined)) {
            return rankLabel;
        }
        if (value.from === value.to) {
            return `${rankLabel}: ${value.from}`;
        }
        if (typeof value.from === "undefined") {
            return `${rankLabel}: < ${value.to}`;
        }
        if (typeof value.to === "undefined") {
            return `${rankLabel}: > ${value.from}`;
        }
        return `${rankLabel}: ${value.from}-${value.to}`;
    };

    return (
        <Flex
            id="ads-filter-factory-wrapper"
            padding="16px 24px"
            overflowX="auto"
            maxWidth="100%"
            data-automation="ads-report-filter-factory"
            gap="8px"
        >
            <Filters.Provider
                state={state}
                onChange={handleFilterChange}
                events={{
                    onFilterClick: (filterName) => adsTracking.onFilterClick(filterLabels[filterName as AdsFilterName]),
                    onFilterChange: (filterName) =>
                        adsTracking.onFilterApply(filterLabels[filterName as AdsFilterName]),
                }}
            >
                <Filters.Custom
                    name="domains"
                    actionable
                    commitMode="close"
                    closeOnChange
                    tooltip={domainsTooltip}
                    popoverContentProps={{ style: { width: 474, zIndex: 1000 } }}
                    label={createIncludeExcludeLabel({
                        defaultLabel: filterLabels.domains,
                        includedLabel: filterLabels.domains,
                        excludedLabel: filterLabels.domains,
                    })}
                >
                    {(api) => (
                        <DomainsIncludeExcludeRenderer
                            state={api.tempValue}
                            onChange={(next: IIncludeExcludeValue) => api.onTempChange(normalizeIncludeExclude(next))}
                            texts={{
                                includeTitle: translate("monitor_and_protect.page.ads.filters.domains.includeTitle"),
                                excludeTitle: translate("monitor_and_protect.page.ads.filters.domains.excludeTitle"),
                                includePlaceholder: translate(
                                    "monitor_and_protect.page.ads.filters.domains.includePlaceholder",
                                ),
                                excludePlaceholder: translate(
                                    "monitor_and_protect.page.ads.filters.domains.excludePlaceholder",
                                ),
                                searchingText: translate("monitor_and_protect.page.ads.filters.domains.searchingText"),
                                label: filterLabels.domains,
                                labelFallback: filterLabels.domains,
                                singleIncluded: translate(
                                    "monitor_and_protect.page.ads.filters.domains.singleIncluded",
                                ),
                                singleExcluded: translate(
                                    "monitor_and_protect.page.ads.filters.domains.singleExcluded",
                                ),
                            }}
                        />
                    )}
                </Filters.Custom>
                <Filters.MultiSelect
                    name="adType"
                    label={createMultiSelectLabel(
                        filterLabels.adType,
                        (id) => adTypeOptions.find((o) => o.value === id),
                        (option) => option.label,
                    )}
                    withSearch={false}
                    options={adTypeOptions}
                    popoverContentProps={{ style: { zIndex: 1000 } }}
                />
                <Filters.Custom
                    name="keywords"
                    actionable
                    commitMode="close"
                    closeOnChange
                    popoverContentProps={{ style: { zIndex: 1000 } }}
                    label={createIncludeExcludeLabel({
                        defaultLabel: filterLabels.keywords,
                        includedLabel: filterLabels.keywords,
                        excludedLabel: filterLabels.keywords,
                    })}
                >
                    {(api) => (
                        <KeywordsIncludeExcludeRenderer
                            state={api.tempValue}
                            onChange={(next: IIncludeExcludeValue) => api.onTempChange(normalizeIncludeExclude(next))}
                            texts={{
                                includeTitle: translate("monitor_and_protect.page.ads.filters.keywords.includeTitle"),
                                excludeTitle: translate("monitor_and_protect.page.ads.filters.keywords.excludeTitle"),
                                includePlaceholder: translate(
                                    "monitor_and_protect.page.ads.filters.keywords.includePlaceholder",
                                ),
                                excludePlaceholder: translate(
                                    "monitor_and_protect.page.ads.filters.keywords.excludePlaceholder",
                                ),
                                addButtonText: translate("monitor_and_protect.page.ads.filters.keywords.addButtonText"),
                            }}
                        />
                    )}
                </Filters.Custom>
                <Filters.Custom
                    name="adContent"
                    popoverContentProps={{ style: { width: 600, zIndex: 1000 } }}
                    label={(value) =>
                        value && value.length > 0
                            ? `${filterLabels.adContent}: ${value.length}`
                            : filterLabels.adContent
                    }
                >
                    {(api) => {
                        const handleRulesChange = (rules: IDynamicFilterRule[]) =>
                            api.onChange(cleanAdContent(rules as unknown as IAdsContentRule[]));

                        return (
                            <MultiIncludeExcludeFilter
                                // Remount to reset the internal rules when the factory's Clear empties the temp value
                                key={api.tempValue ? "has-rules" : "empty"}
                                initialRules={
                                    (api.tempValue as unknown as IDynamicFilterRule[]) ??
                                    (DEFAULT_AD_CONTENT_RULES as unknown as IDynamicFilterRule[])
                                }
                                withFooter={false}
                                maxRules={3}
                                maxRulesBannerText={translate(
                                    "monitor_and_protect.page.ads.filters.adContent.maxRulesText",
                                )}
                                inputTextPlaceholder={translate(
                                    "monitor_and_protect.page.ads.filters.adContent.inputPlaceholder",
                                )}
                                dropDownsOptions={adContentDropDownsOptions}
                                onUpdateRules={handleRulesChange}
                                onDeleteRow={(_index, rules) => handleRulesChange(rules)}
                            />
                        );
                    }}
                </Filters.Custom>
                <Filters.Range
                    name="avgRank"
                    closeOnChange
                    label={renderRankLabel}
                    popoverContentProps={{ style: { width: 300, zIndex: 1000 } }}
                    properties={{
                        min: 1,
                        step: 1,
                        max: 101,
                        from: {
                            inputProperties: {
                                placeholder: translate("monitor_and_protect.page.ads.filters.rank.placeholder.from"),
                            },
                        },
                        to: {
                            inputProperties: {
                                placeholder: translate("monitor_and_protect.page.ads.filters.rank.placeholder.to"),
                            },
                        },
                    }}
                />
                <Filters.ClearAll />
            </Filters.Provider>
        </Flex>
    );
};
